import React, { useState, useEffect } from 'react';
import { Table, Button, Badge, Card, Row, Col, Form, InputGroup, Alert, Modal, Dropdown } from 'react-bootstrap';
import { Search, Edit, Trash, PlusCircle, RefreshCw, Image, Box, Package, XCircle, Download, Tag } from 'lucide-react';
import * as Icons from 'lucide-react';
import { getAllProducts, deleteProduct, deleteMultipleProducts } from '../../api/products';
import { createCategory, getAllCategories, updateCategory } from '../../api/categories';
import useAuth from '../../hooks/useAuth';
import ProductDetails from './ProductDetails';
import { isSalesRep } from '../../api/auth';
import { getDistributorPricesCurrentUser } from '../../api/partner';

const API_BASE_URL = process.env.REACT_APP_MEDIA_URL || 'http://localhost:8080';

// Jeu d'icônes Lucide proposées pour les catégories
const CATEGORY_ICONS = [
    'Package', 'Box', 'Boxes', 'Container', 'Archive', 'Tag', 'Tags',
    'ShoppingBag', 'ShoppingCart', 'Gift', 'Layers', 'Coffee', 'Utensils',
    'CupSoda', 'GlassWater', 'Soup', 'Refrigerator', 'Droplet', 'Trash2',
    'Wrench', 'Home', 'Shirt', 'Baby', 'Flower2',
];

// Couleurs proposées pour les badges de catégorie
const CATEGORY_COLORS = [
    '#0d6efd', '#6610f2', '#6f42c1', '#d63384', '#dc3545', '#fd7e14',
    '#ffc107', '#198754', '#20c997', '#0dcaf0', '#6c757d', '#343a40',
];

const ProductList = ({ onAddProduct, onEditProduct }) => {
    const { isAdmin, isManager, isSalesRep, isDistributor, user } = useAuth();
    const hasStockAccess = isAdmin || isManager || isSalesRep;
    const [priceList, setPriceList] = useState([]);
    const [loadingPrices, setLoadingPrices] = useState(true);
    const [products, setProducts] = useState([]);
    const [filteredProducts, setFilteredProducts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const [refreshTrigger, setRefreshTrigger] = useState(0);
    const [selectedProducts, setSelectedProducts] = useState([]);
    const [selectAll, setSelectAll] = useState(false);
    const [showExportModal, setShowExportModal] = useState(false);
    const [selectedColumns, setSelectedColumns] = useState(['name', 'sku', 'basePrice', 'stockQuantity', 'category', 'dimensions']);

    // Catégories + modal de création/modification
    const [categories, setCategories] = useState([]);
    const [showCategoryModal, setShowCategoryModal] = useState(false);
    const [editingCategoryId, setEditingCategoryId] = useState(null);
    const [categoryForm, setCategoryForm] = useState({ name: '', icon: '', color: '#0d6efd', description: '' });
    const [categorySaving, setCategorySaving] = useState(false);
    const [categoryError, setCategoryError] = useState('');
    const [categorySuccess, setCategorySuccess] = useState('');

    // Nouveaux états pour la recherche par colonne
    const [columnFilters, setColumnFilters] = useState({
        numeroProduit: '',
        name: '',
        sku: '',
        codeBarre: '',
        category: '',
        price: '',
        stock: ''
    });

    // Définition des colonnes disponibles pour l'export
    const availableColumns = [
        { key: 'name', label: 'name', valueGetter: (product) => product.name },
        { key: 'sku', label: 'sku', valueGetter: (product) => product.sku },
        { key: 'codeBarre', label: 'Code barre', valueGetter: (product) => product.codeBarre || '' },
        { key: 'basePrice', label: 'basePrice', valueGetter: (product) => product.basePrice },
        ...((isAdmin || isManager) ? [{ key: 'prixFournisseur', label: 'prixFournisseur', valueGetter: (product) => product.prixFournisseur ?? '' }] : []),
        { key: 'priceWithVat', label: 'priceWithVat', valueGetter: (product) => (product.basePrice * 1.18).toFixed(2) },
        { key: 'stockQuantity', label: 'stockQuantity', valueGetter: (product) => product.stockQuantity },
        { key: 'category', label: 'category', valueGetter: (product) => product.category?.name || '' },
        { key: 'dimensions', label: 'dimensions', valueGetter: (product) => product.dimensions || '' },
        { key: 'createdAt', label: 'createdAt', valueGetter: (product) => product.createdAt ? new Date(product.createdAt).toLocaleDateString() : '' },
        { key: 'updatedAt', label: 'updatedAt', valueGetter: (product) => product.updatedAt ? new Date(product.updatedAt).toLocaleDateString() : '' }
    ];

    const exportToCSV = () => {
        let productsToExport = [];
        if (selectedProducts.length > 0) {
            productsToExport = filteredProducts.filter(product => selectedProducts.includes(product.id));
        } else {
            productsToExport = filteredProducts;
        }

        if (productsToExport.length === 0) {
            alert('Aucun produit à exporter');
            return;
        }

        const selectedColumnObjects = availableColumns.filter(col => selectedColumns.includes(col.key));
        const headers = selectedColumnObjects.map(col => col.label);
        const rows = productsToExport.map(product => {
            return selectedColumnObjects.map(col => col.valueGetter(product));
        });

        const separator = ';';
        let csvContent = '\uFEFF';
        csvContent += headers.join(separator) + '\n';

        rows.forEach(row => {
            const escapedRow = row.map(value => {
                const strValue = String(value);
                if (strValue.includes(separator) || strValue.includes('"') || strValue.includes('\n')) {
                    return `"${strValue.replace(/"/g, '""')}"`;
                }
                return strValue;
            });
            csvContent += escapedRow.join(separator) + '\n';
        });

        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);

        link.setAttribute('href', url);
        link.setAttribute('download', `products_export_${new Date().toISOString().slice(0, 10)}.csv`);
        link.style.visibility = 'hidden';

        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        setShowExportModal(false);
    };

    const handleColumnSelection = (columnKey) => {
        if (selectedColumns.includes(columnKey)) {
            setSelectedColumns(selectedColumns.filter(key => key !== columnKey));
        } else {
            setSelectedColumns([...selectedColumns, columnKey]);
        }
    };

    const ExportModal = () => (
        <Modal show={showExportModal} onHide={() => setShowExportModal(false)}>
            <Modal.Header closeButton>
                <Modal.Title>Options d'export</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <div className="mb-3">
                    <h6>Produits à exporter :</h6>
                    <p className="text-muted">
                        {selectedProducts.length > 0
                            ? `${selectedProducts.length} produit(s) sélectionné(s)`
                            : `Tous les produits filtrés (${filteredProducts.length})`
                        }
                    </p>
                </div>

                <div>
                    <h6>Colonnes à inclure :</h6>
                    {availableColumns.map(column => (
                        <Form.Check
                            key={column.key}
                            type="checkbox"
                            id={`column-${column.key}`}
                            label={column.label}
                            checked={selectedColumns.includes(column.key)}
                            onChange={() => handleColumnSelection(column.key)}
                            className="mb-2"
                        />
                    ))}
                </div>
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={() => setShowExportModal(false)}>
                    Annuler
                </Button>
                <Button variant="primary" onClick={exportToCSV}>
                    Exporter
                </Button>
            </Modal.Footer>
        </Modal>
    );

    const [showDetails, setShowDetails] = useState(false);
    const [selectedProductId, setSelectedProductId] = useState(null);

    const canModify = isAdmin || isManager;

    useEffect(() => {
        const fetchPrices = async () => {
            if (isDistributor && user?.id) {
                try {
                    const prices = await getDistributorPricesCurrentUser(user.id);
                    setPriceList(prices);
                } catch (err) {
                    console.error("Erreur de chargement des prix du distributeur", err);
                } finally {
                    setLoadingPrices(false);
                }
            } else {
                setLoadingPrices(false);
            }
        };

        fetchPrices();
    }, [isDistributor, user]);

    const getDistributorPrice = (productId) => {
    if (!priceList || !Array.isArray(priceList)) {
        console.warn('priceList non défini ou invalide');
        return null;
    }

    const entry = priceList.find(p => String(p?.product?.id) === String(productId));
    
    if (!entry) {
        console.warn(`Aucun prix trouvé pour le produit ${productId}`);
    }

    return entry?.price ?? null;
};

    // Chargement des catégories (pour le filtre + édition)
    const loadCategories = async () => {
        try {
            const data = await getAllCategories();
            setCategories(Array.isArray(data) ? data : []);
        } catch (e) {
            console.error('Erreur chargement catégories:', e);
        }
    };

    useEffect(() => {
        loadCategories();
    }, []);

    useEffect(() => {
    const fetchProducts = async () => {
        try {
            setLoading(true);
            setError('');
            const data = await getAllProducts();

            // Trier par numeroProduit en ordre croissant
            const sortedData = [...data].sort((a, b) => {
                const numA = Number(a.numeroProduit);
                const numB = Number(b.numeroProduit);
                return numA - numB;
            });

            setProducts(sortedData);
            setFilteredProducts(sortedData);
        } catch (err) {
            setError('Failed to load products. Please try again.');
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    fetchProducts();
}, [refreshTrigger]);

    // Fonction de filtrage mise à jour pour utiliser les filtres par colonne ET la recherche globale
    useEffect(() => {
        let filtered = products;

        // Appliquer la recherche globale d'abord
        if (searchTerm.trim() !== '') {
            filtered = filtered.filter(product =>
                product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                product.sku.toLowerCase().includes(searchTerm.toLowerCase()) ||
                (product.codeBarre && product.codeBarre.toLowerCase().includes(searchTerm.toLowerCase())) ||
                (product.category?.name && product.category.name.toLowerCase().includes(searchTerm.toLowerCase()))
            );
        }

        // Appliquer ensuite les filtres par colonne
        if (columnFilters?.numeroProduit) {
            filtered = filtered.filter(product =>
                product?.numeroProduit?.toString().toLowerCase().includes(columnFilters?.numeroProduit.toLowerCase())
            );
        }

        if (columnFilters.name) {
            filtered = filtered.filter(product =>
                product.name.toLowerCase().includes(columnFilters.name.toLowerCase())
            );
        }

        if (columnFilters.sku) {
            filtered = filtered.filter(product =>
                product.sku.toLowerCase().includes(columnFilters.sku.toLowerCase())
            );
        }

        if (columnFilters.codeBarre) {
            filtered = filtered.filter(product =>
                (product.codeBarre || '').toLowerCase().includes(columnFilters.codeBarre.toLowerCase())
            );
        }

        if (columnFilters.category) {
            filtered = filtered.filter(product =>
                (product.category?.name || '').toLowerCase().includes(columnFilters.category.toLowerCase())
            );
        }

        if (columnFilters.price) {
            filtered = filtered.filter(product => {
                const price = isDistributor && getDistributorPrice(product.id)
                    ? getDistributorPrice(product.id)
                    : product.basePrice;
                return price.toString().includes(columnFilters.price);
            });
        }

        if (columnFilters.stock && hasStockAccess) {
            filtered = filtered.filter(product =>
                product.stockQuantity.toString().includes(columnFilters.stock)
            );
        }

        setFilteredProducts(filtered);
    }, [searchTerm, products, columnFilters, isDistributor, priceList, hasStockAccess]);

    useEffect(() => {
        setSelectedProducts([]);
        setSelectAll(false);
    }, [filteredProducts]);

    const handleRefresh = () => {
        setRefreshTrigger(prev => prev + 1);
    };

    const handleDelete = async (id) => {
        if (window.confirm('Are you sure you want to delete this product?')) {
            try {
                await deleteProduct(id);
                setProducts(products.filter(product => product.id !== id));
                setFilteredProducts(filteredProducts.filter(product => product.id !== id));
            } catch (err) {
                setError('Failed to delete product. Please try again.');
                console.error(err);
            }
        }
    };

    const handleSelectAll = (e) => {
        const isChecked = e.target.checked;
        setSelectAll(isChecked);

        if (isChecked) {
            setSelectedProducts(filteredProducts.map(product => product.id));
        } else {
            setSelectedProducts([]);
        }
    };

    const handleSelectProduct = (id) => {
        if (selectedProducts.includes(id)) {
            setSelectedProducts(selectedProducts.filter(productId => productId !== id));
        } else {
            setSelectedProducts([...selectedProducts, id]);
        }
    };

    const handleDeleteSelected = async () => {
        if (selectedProducts.length === 0) return;

        if (window.confirm(`Are you sure you want to delete ${selectedProducts.length} selected products?`)) {
            try {
                await deleteMultipleProducts(selectedProducts);

                const updatedProducts = products.filter(product => !selectedProducts.includes(product.id));
                setProducts(updatedProducts);
                setFilteredProducts(filteredProducts.filter(product => !selectedProducts.includes(product.id)));

                setSelectedProducts([]);
                setSelectAll(false);
            } catch (err) {
                setError('Failed to delete selected products. Please try again.');
                console.error(err);
            }
        }
    };

    const handleViewProduct = (id) => {
        setSelectedProductId(id);
        setShowDetails(true);
    };

    const getMainImageUrl = (product) => {
        if (!product.images || product.images.length === 0) {
            return null;
        }

        const mainImage = product.images.find(img => img.mainImage);
        const path = mainImage ? mainImage.path : product.images[0].path;

        if (path.startsWith('http://') || path.startsWith('https://')) {
            return path;
        }

        if (path.startsWith('uploads/')) {
            return `${API_BASE_URL}/${path}`;
        }

        return `${API_BASE_URL}/uploads/${path}`;
    };

    // Fonction pour gérer les changements dans les filtres par colonne
    const handleColumnFilterChange = (column, value) => {
        setColumnFilters(prev => ({
            ...prev,
            [column]: value
        }));
    };

    // Fonction pour réinitialiser tous les filtres
    const clearAllFilters = () => {
        setSearchTerm('');
        setColumnFilters({
            numeroProduit: '',
            name: '',
            sku: '',
            codeBarre: '',
            category: '',
            price: '',
            stock: ''
        });
    };

    // Badge de catégorie : icône (Lucide) + nom + couleur
    const renderCategoryBadge = (category) => {
        if (!category) return <span className="text-muted">-</span>;
        const Icon = category.icon && Icons[category.icon] ? Icons[category.icon] : null;
        return (
            <Badge
                style={{
                    backgroundColor: category.color || '#6c757d',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 4,
                }}
            >
                {Icon && <Icon size={12} />}
                {category.name}
            </Badge>
        );
    };

    // Ouvrir le modal en mode création
    const openCreateCategoryModal = () => {
        setEditingCategoryId(null);
        setCategoryError('');
        setCategorySuccess('');
        setCategoryForm({ name: '', icon: '', color: '#0d6efd', description: '' });
        setShowCategoryModal(true);
    };

    // Ouvrir le modal en mode modification
    const openEditCategoryModal = (cat) => {
        setEditingCategoryId(cat.id);
        setCategoryError('');
        setCategorySuccess('');
        setCategoryForm({
            name: cat.name || '',
            icon: cat.icon || '',
            color: cat.color || '#0d6efd',
            description: cat.description || '',
        });
        setShowCategoryModal(true);
    };

    const handleSaveCategory = async () => {
        if (!categoryForm.name.trim()) {
            setCategoryError('Le nom de la catégorie est obligatoire');
            return;
        }
        try {
            setCategorySaving(true);
            setCategoryError('');
            const payload = {
                name: categoryForm.name.trim(),
                icon: categoryForm.icon || null,
                color: categoryForm.color || null,
                description: categoryForm.description || null,
            };

            if (editingCategoryId) {
                await updateCategory(editingCategoryId, payload);
                setCategorySuccess('Catégorie modifiée avec succès');
            } else {
                await createCategory(payload);
                setCategorySuccess('Catégorie créée avec succès');
            }

            await loadCategories();
            // Recharger les produits pour refléter une catégorie modifiée (nom/icône/couleur)
            if (editingCategoryId) handleRefresh();

            setTimeout(() => {
                setShowCategoryModal(false);
                setCategorySuccess('');
                setEditingCategoryId(null);
            }, 900);
        } catch (err) {
            const msg = typeof err === 'string'
                ? err
                : (err?.message || err?.error || "Erreur lors de l'enregistrement de la catégorie");
            setCategoryError(msg);
        } finally {
            setCategorySaving(false);
        }
    };

    const renderProductsCards = () => {
    if (!Array.isArray(filteredProducts) || filteredProducts.length === 0) {
        return <div className="text-center text-muted py-4">Aucun produit trouvé</div>;
    }

    return filteredProducts.map((product) => {
        const imageUrl = getMainImageUrl(product);
        return (
            <Card key={product.id} className="mb-2 shadow-sm">
                <Card.Body className="p-3">
                    <div className="d-flex">
                        {canModify && (
                            <Form.Check
                                type="checkbox"
                                className="me-2"
                                checked={selectedProducts.includes(product.id)}
                                onChange={() => handleSelectProduct(product.id)}
                            />
                        )}

                        {/* Image / placeholder */}
                        <div
                            className="me-3 flex-shrink-0"
                            style={{ width: 50, height: 50, cursor: 'pointer' }}
                            onClick={() => handleViewProduct(product.id)}
                        >
                            {imageUrl ? (
                                <img
                                    src={imageUrl}
                                    alt={product.name}
                                    style={{ width: 50, height: 50, objectFit: 'cover', borderRadius: 4 }}
                                />
                            ) : (
                                <div
                                    className="border d-flex align-items-center justify-content-center"
                                    style={{ width: 50, height: 50, borderRadius: 4 }}
                                >
                                    <Package size={20} className="text-muted" />
                                </div>
                            )}
                        </div>

                        <div className="flex-grow-1">
                            <div className="fw-bold">{product.name}</div>
                            <small className="text-muted d-block">Réf : {product.sku}</small>
                            {product.codeBarre && (
                                <small className="text-muted d-block">Code-barres : {product.codeBarre}</small>
                            )}
                            {canModify && product.prixFournisseur != null && (
                                <small className="text-muted d-block">Prix fournisseur : {product.prixFournisseur} F</small>
                            )}
                            {product.category && (
                                <div className="mt-1">{renderCategoryBadge(product.category)}</div>
                            )}

                            <div className="mt-1">
                                <span className="fw-semibold">
                                    {isDistributor ? (
                                        loadingPrices ? (
                                            'Chargement...'
                                        ) : getDistributorPrice(product.id) != null &&
                                          getDistributorPrice(product.id) != 0 ? (
                                            <>
                                                {getDistributorPrice(product.id).toFixed()} F{' '}
                                                <Badge bg="success">Prix spécial</Badge>
                                            </>
                                        ) : (
                                            `${product.basePrice} F`
                                        )
                                    ) : (
                                        `${product.basePrice.toFixed()} F`
                                    )}
                                </span>

                                {hasStockAccess && (
                                    <span className="ms-2">
                                        <span className="text-muted small">Stock : </span>
                                        {product.stockQuantity <= 10 ? (
                                            <Badge bg="danger">{product.stockQuantity}</Badge>
                                        ) : (
                                            <Badge bg="success">{product.stockQuantity}</Badge>
                                        )}
                                    </span>
                                )}
                            </div>
                        </div>
                    </div>

                    <div className="d-flex gap-2 mt-3">
                        <Button variant="outline-info" size="sm" onClick={() => handleViewProduct(product.id)}>
                            <Image size={14} className="me-1" /> Voir
                        </Button>
                        {canModify && (
                            <>
                                <Button variant="outline-primary" size="sm" onClick={() => onEditProduct(product.id)}>
                                    <Edit size={14} className="me-1" /> Modifier
                                </Button>
                                <Button variant="outline-danger" size="sm" onClick={() => handleDelete(product.id)}>
                                    <Trash size={14} className="me-1" /> Supprimer
                                </Button>
                            </>
                        )}
                    </div>
                </Card.Body>
            </Card>
        );
    });
};

    return (
        <>
            <Card className="shadow-sm">
                <Card.Header>
                    <Row className="align-items-center">
                        <Col>
                        </Col>
                        <Col xs="auto">
                            {canModify && (
                                <Button
                                    variant="outline-primary"
                                    size="sm"
                                    className="me-2"
                                    onClick={openCreateCategoryModal}
                                >
                                    <Tag size={16} className="me-1" />
                                    Nouvelle catégorie
                                </Button>
                            )}

                            <Button
                                variant="outline-success"
                                size="sm"
                                className="me-2"
                                onClick={() => setShowExportModal(true)}
                            >
                                <Download size={16} className="me-1" />
                                Exporter
                            </Button>
                            
                            <Button
                                variant="outline-secondary"
                                size="sm"
                                onClick={handleRefresh}
                            >
                                <RefreshCw size={16} />
                            </Button>
                        </Col>
                    </Row>
                </Card.Header>
                <Card.Body>
                    {error && <Alert variant="danger">{error}</Alert>}

                    <Row className="mb-3">
                        <Col md={8}>
                            <InputGroup>
                                <InputGroup.Text>
                                    <Search size={18} />
                                </InputGroup.Text>
                                <Form.Control
                                    placeholder="Rechercher un produit par son nom, réf, code-barres ou catégorie..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                />
                            </InputGroup>
                        </Col>
                        <Col md={2} className="mt-2 mt-md-0">
                            <Button
                                variant="outline-secondary"
                                onClick={clearAllFilters}
                                className="w-100"
                                size="sm"
                            >
                                Effacer filtres
                            </Button>
                        </Col>
                        <Col md={2} className="mt-2 mt-md-0">
                            {canModify && selectedProducts.length > 0 && (
                                <Button
                                    variant="danger"
                                    onClick={handleDeleteSelected}
                                    className="w-100"
                                    size="sm"
                                >
                                    <XCircle size={16} className="me-1" />
                                    Supprimer ({selectedProducts.length})
                                </Button>
                            )}
                        </Col>
                    </Row>

                    {loading ? (
                        <div className="text-center py-4">
                            <div className="spinner-border text-primary" role="status">
                                <span className="visually-hidden">Chargement...</span>
                            </div>
                            <p className="mt-2">Produits en chargement...</p>
                        </div>
                    ) : (
                        <>
                        <div className="d-none d-lg-block">

                        <div className="table-responsive">
                            <Table striped hover>
                                <thead>
                                    <tr>
                                        {canModify && (
                                            <th width="50">
                                                <Form.Check
                                                    type="checkbox"
                                                    checked={selectAll}
                                                    onChange={handleSelectAll}
                                                />
                                            </th>
                                        )}
                                        <th width="80">Numéro</th>
                                        <th width="80">Image</th>
                                        <th>Nom</th>
                                        <th>Référence</th>
                                        <th>Code-barres</th>
                                        <th>Catégorie</th>
                                        <th>Prix</th>
                                        {canModify && <th>Prix fournisseur</th>}
                                        <th hidden={!hasStockAccess}>Stock</th>
                                        <th width="150">Actions</th>
                                    </tr>
                                    {/* Ligne de filtres par colonne */}
                                    <tr>
                                        {canModify && (
                                            <th>
                                                {/* Cellule vide pour la colonne de sélection */}
                                            </th>
                                        )}
                                        <th width="80">
                                            <Form.Control
                                                size="sm"
                                                type="text"
                                                placeholder="Num..."
                                                value={columnFilters?.numeroProduit}
                                                onChange={(e) => handleColumnFilterChange('numeroProduit', e.target.value)}
                                                style={{ minWidth: '60px' }}
                                            />
                                        </th>
                                        <th>
                                            {/* Cellule vide pour la colonne image */}
                                        </th>
                                        <th>
                                            <Form.Control
                                                size="sm"
                                                type="text"
                                                placeholder="Filtrer nom..."
                                                value={columnFilters.name}
                                                onChange={(e) => handleColumnFilterChange('name', e.target.value)}
                                            />
                                        </th>
                                        <th>
                                            <Form.Control
                                                size="sm"
                                                type="text"
                                                placeholder="Filtrer réf..."
                                                value={columnFilters.sku}
                                                onChange={(e) => handleColumnFilterChange('sku', e.target.value)}
                                            />
                                        </th>
                                        <th>
                                            <Form.Control
                                                size="sm"
                                                type="text"
                                                placeholder="Filtrer code-barres..."
                                                value={columnFilters.codeBarre}
                                                onChange={(e) => handleColumnFilterChange('codeBarre', e.target.value)}
                                            />
                                        </th>
                                        <th style={{ minWidth: '170px' }}>
                                            {/* Filtre catégorie : liste déroulante avec icône d'édition par catégorie */}
                                            <Dropdown>
                                                <Dropdown.Toggle
                                                    size="sm"
                                                    variant="outline-secondary"
                                                    className="w-100 text-truncate"
                                                >
                                                    {columnFilters.category || 'Catégorie'}
                                                </Dropdown.Toggle>
                                                <Dropdown.Menu style={{ maxHeight: 300, overflowY: 'auto', minWidth: 240 }}>
                                                    <Dropdown.Item onClick={() => handleColumnFilterChange('category', '')}>
                                                        Toutes
                                                    </Dropdown.Item>
                                                    <Dropdown.Divider />
                                                    {categories.length === 0 && (
                                                        <div className="px-3 py-1 text-muted small">Aucune catégorie</div>
                                                    )}
                                                    {categories.map((cat) => (
                                                        <div
                                                            key={cat.id}
                                                            className="d-flex align-items-center justify-content-between px-2 py-1"
                                                        >
                                                            <span
                                                                style={{ cursor: 'pointer', flex: 1 }}
                                                                onClick={() => handleColumnFilterChange('category', cat.name)}
                                                            >
                                                                {renderCategoryBadge(cat)}
                                                            </span>
                                                            {canModify && (
                                                                <Button
                                                                    variant="link"
                                                                    size="sm"
                                                                    className="p-0 ms-2 text-secondary"
                                                                    title="Modifier la catégorie"
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        openEditCategoryModal(cat);
                                                                    }}
                                                                >
                                                                    <Edit size={14} />
                                                                </Button>
                                                            )}
                                                        </div>
                                                    ))}
                                                </Dropdown.Menu>
                                            </Dropdown>
                                        </th>
                                        <th>
                                            <Form.Control
                                                size="sm"
                                                type="text"
                                                placeholder="Filtrer prix..."
                                                value={columnFilters.price}
                                                onChange={(e) => handleColumnFilterChange('price', e.target.value)}
                                            />
                                        </th>
                                        {canModify && <th></th>}
                                        <th hidden={!hasStockAccess}>
                                            <Form.Control
                                                size="sm"
                                                type="text"
                                                placeholder="Filtrer stock..."
                                                value={columnFilters.stock}
                                                onChange={(e) => handleColumnFilterChange('stock', e.target.value)}
                                            />
                                        </th>
                                        
                                        <th>
                                            {/* Cellule vide pour la colonne actions */}
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {Array.isArray(filteredProducts) && filteredProducts.length > 0 ? (
                                        filteredProducts.map(product => (
                                            <tr key={product.id}>
                                                {canModify && (
                                                    <td>
                                                        <Form.Check
                                                            type="checkbox"
                                                            checked={selectedProducts.includes(product.id)}
                                                            onChange={() => handleSelectProduct(product.id)}
                                                        />
                                                    </td>
                                                )}
                                                <td>{product.numeroProduit ? product.numeroProduit : null}</td>
                                                <td>
                                                    {getMainImageUrl(product) ? (
                                                        <div
                                                            style={{
                                                                width: '50px',
                                                                height: '50px',
                                                                backgroundImage: `url("${getMainImageUrl(product)}")`,
                                                                backgroundSize: 'cover',
                                                                backgroundPosition: 'center',
                                                                borderRadius: '4px',
                                                                cursor: 'pointer'
                                                            }}
                                                            onClick={() => handleViewProduct(product.id)}
                                                            onError={(e) => {
                                                                e.target.onerror = null;
                                                                e.target.style.backgroundImage = 'none';
                                                                e.target.style.display = 'flex';
                                                                e.target.style.justifyContent = 'center';
                                                                e.target.style.alignItems = 'center';
                                                                e.target.innerHTML = '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="2" y="2" width="20" height="20" rx="2"/></svg>';
                                                            }}
                                                        />
                                                    ) : (
                                                        <div
                                                            className="d-flex justify-content-center align-items-center bg-light"
                                                            style={{
                                                                width: '50px',
                                                                height: '50px',
                                                                borderRadius: '4px',
                                                                cursor: 'pointer'
                                                            }}
                                                            onClick={() => handleViewProduct(product.id)}
                                                        >
                                                            <Package size={20} className="text-muted" />
                                                        </div>
                                                    )}
                                                </td>
                                                <td>{product.name}</td>
                                                <td>{product.sku}</td>
                                                <td>{product.codeBarre || '-'}</td>
                                                <td>{renderCategoryBadge(product.category)}</td>
                                                <td>
                                                    {isDistributor ? (
                                                        loadingPrices ? (
                                                            <span>Chargement...</span>
                                                        ) : getDistributorPrice(product.id) != null && getDistributorPrice(product.id) != 0 ? (
                                                            <>
                                                                {getDistributorPrice(product.id).toFixed()} F{" "}
                                                                <span className="ms-1 badge bg-success">Prix spécial</span>
                                                            </>
                                                        ) : (
                                                            `${product.basePrice} F`
                                                        )
                                                    ) : (
                                                        `${product.basePrice.toFixed()} F`
                                                    )}
                                                </td>
                                                {canModify && (
                                                    <td>{product.prixFournisseur != null ? `${product.prixFournisseur} F` : '-'}</td>
                                                )}
                                                <td hidden={!hasStockAccess}>
                                                    {product.stockQuantity <= 10 ? (
                                                        <Badge bg="danger">{product.stockQuantity}</Badge>
                                                    ) : (
                                                        <Badge bg="success">{product.stockQuantity}</Badge>
                                                    )}
                                                </td>
                                                <td style={{ whiteSpace: 'nowrap' }}>
                                                    <Button
                                                        variant="outline-info"
                                                        size="sm"
                                                        className="me-1"
                                                        onClick={() => handleViewProduct(product.id)}
                                                    >
                                                        <Image size={14} />
                                                    </Button>
                                                    {canModify && (
                                                        <>
                                                            <Button
                                                                variant="outline-primary"
                                                                size="sm"
                                                                className="me-1"
                                                                onClick={() => onEditProduct(product.id)}
                                                            >
                                                                <Edit size={14} />
                                                            </Button>
                                                            <Button
                                                                variant="outline-danger"
                                                                size="sm"
                                                                onClick={() => handleDelete(product.id)}
                                                            >
                                                                <Trash size={14} />
                                                            </Button>
                                                        </>
                                                    )}
                                                </td>
                                            </tr>
                                        ))
                                    ) : (
                                        <tr>
                                            <td colSpan={canModify ? "11" : "9"} className="text-center py-3">
                                                Aucun produit trouvé
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </Table>
                        </div>
                        </div>
                        {/* < lg : cartes */}
        <div className="d-lg-none">
            {renderProductsCards()}
        </div>
        </>
                        
                    )}
                </Card.Body>
            </Card>

            <ProductDetails
                productId={selectedProductId}
                show={showDetails}
                onHide={() => setShowDetails(false)}
            />
            <ExportModal />

            {/* Modal de création / modification de catégorie */}
            <Modal show={showCategoryModal} onHide={() => setShowCategoryModal(false)} centered>
                <Modal.Header closeButton>
                    <Modal.Title>{editingCategoryId ? 'Modifier la catégorie' : 'Nouvelle catégorie'}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {categoryError && <Alert variant="danger">{categoryError}</Alert>}
                    {categorySuccess && <Alert variant="success">{categorySuccess}</Alert>}

                    <Form.Group className="mb-3">
                        <Form.Label>Nom *</Form.Label>
                        <Form.Control
                            type="text"
                            value={categoryForm.name}
                            onChange={(e) => setCategoryForm(prev => ({ ...prev, name: e.target.value }))}
                            placeholder="Ex : Vaisselle, Rangement…"
                            autoFocus
                        />
                    </Form.Group>

                    <Form.Group className="mb-3">
                        <Form.Label>Icône</Form.Label>
                        <div className="d-flex flex-wrap gap-2">
                            {CATEGORY_ICONS.map((name) => {
                                const Icon = Icons[name];
                                const selected = categoryForm.icon === name;
                                return (
                                    <Button
                                        key={name}
                                        type="button"
                                        variant={selected ? 'primary' : 'outline-secondary'}
                                        size="sm"
                                        onClick={() => setCategoryForm(prev => ({ ...prev, icon: selected ? '' : name }))}
                                        style={{ width: 40, height: 40, padding: 0 }}
                                        title={name}
                                    >
                                        {Icon && <Icon size={18} />}
                                    </Button>
                                );
                            })}
                        </div>
                    </Form.Group>

                    <Form.Group className="mb-3">
                        <Form.Label>Couleur</Form.Label>
                        <div className="d-flex align-items-center flex-wrap gap-2">
                            {CATEGORY_COLORS.map((c) => (
                                <span
                                    key={c}
                                    onClick={() => setCategoryForm(prev => ({ ...prev, color: c }))}
                                    style={{
                                        width: 26,
                                        height: 26,
                                        borderRadius: '50%',
                                        backgroundColor: c,
                                        cursor: 'pointer',
                                        border: categoryForm.color === c ? '3px solid #000' : '1px solid #ccc',
                                    }}
                                    title={c}
                                />
                            ))}
                            <Form.Control
                                type="color"
                                value={categoryForm.color}
                                onChange={(e) => setCategoryForm(prev => ({ ...prev, color: e.target.value }))}
                                style={{ width: 44, height: 32, padding: 2 }}
                                title="Couleur personnalisée"
                            />
                        </div>
                    </Form.Group>

                    <Form.Group className="mb-3">
                        <Form.Label>Description</Form.Label>
                        <Form.Control
                            as="textarea"
                            rows={2}
                            value={categoryForm.description}
                            onChange={(e) => setCategoryForm(prev => ({ ...prev, description: e.target.value }))}
                            placeholder="Optionnel"
                        />
                    </Form.Group>

                    <div className="border rounded p-2 bg-light d-flex align-items-center gap-2">
                        <small className="text-muted">Aperçu :</small>
                        {renderCategoryBadge({
                            name: categoryForm.name || 'Catégorie',
                            icon: categoryForm.icon,
                            color: categoryForm.color,
                        })}
                    </div>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowCategoryModal(false)} disabled={categorySaving}>
                        Annuler
                    </Button>
                    <Button variant="primary" onClick={handleSaveCategory} disabled={categorySaving}>
                        {categorySaving ? 'Enregistrement…' : (editingCategoryId ? 'Enregistrer' : 'Créer')}
                    </Button>
                </Modal.Footer>
            </Modal>
        </>
    );
};

export default ProductList;